import { createContext, Dispatch, FC, ReactNode, SetStateAction, useCallback, useMemo, useState } from 'react';

type BookingForContextValue = {
  /** Id of the user the current booking is for, or undefined when booking for oneself. */
  bookForUserId: string | undefined;
  /** Set (or clear, with undefined) the user the booking is for. */
  setBookForUserId: Dispatch<SetStateAction<string | undefined>>;
  /** Reset to booking for oneself. */
  clearBookForUser: () => void;
};

export const BookingForContext = createContext<BookingForContextValue | null>(null);

/**
 * Holds "who the current manual booking is for" — the traveler an admin is booking on behalf of, or undefined (the
 * default) when booking for oneself. Deliberately in-memory only: it carries the selection through a single booking
 * (search → basket, which are SPA navigations, not reloads) but is NOT persisted, so landing on / reloading any page
 * always starts back at "Myself" rather than sticking to whoever was last picked.
 */
export const BookingForProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [bookForUserId, setBookForUserId] = useState<string | undefined>(undefined);

  const clearBookForUser = useCallback(() => setBookForUserId(undefined), []);

  const value = useMemo(
    () => ({ bookForUserId, setBookForUserId, clearBookForUser }),
    [bookForUserId, clearBookForUser],
  );

  return <BookingForContext.Provider value={value}>{children}</BookingForContext.Provider>;
};
